import { get } from "../../utils/OfflineUtil";
import store from "../../redux-store";
import {
  getOptionSetsDataSuccess,
  optionSetsDataCallComplete,
} from "../../redux-store/slices/catalogueSlice";
import {
  getItemsDataSuccess,
  itemsDataCallComplete,
} from "../../redux-store/slices/ItemsSlice";
/**
 * Uplifted pieces of `helper/OfflineHelper` (apps/web + apps/mobile) — WEB half.
 *
 * The common part lives in `./shared` (`handleOfflineError`, `replaceLocalSale`,
 * `saveOptionSetsOffline`, `putItemsOffline`, `saveOthersOffline` — all five byte-identical
 * in both apps, diffed function-for-function). Only what genuinely differs is here.
 */
import { getLocalStorage } from "../../utils/localStorage";
import { SALE_SYNC_STATUSES } from "../../utils/constants/shared";
import { handleOfflineError, replaceLocalSale, saveSaleLocally } from "./shared";
import { populateMetaInfo } from "../saleHelpers";

import moment from "moment-timezone";
import { isEmpty } from "lodash";

import { setSyncData } from "../../redux-store/slices/CustomerSlice";
import { ORDERS_CLEANUP_INTERVALS, SALE_STATUS_TYPES } from "../../utils/constants";
import { isOffline } from "../../facade/ServiceFacade";
import { fetchAllOfflineSales, removeOfflineSale } from "./shared";
import { updateOfflineLogin } from "../../redux-store/slices/GlobalSlice";
import { logoutUserSuccess } from "../../redux-store/slices/LoginSlice";
import { logout } from "../../effects-bridge";

export * from "./shared";

/**
 * Read the local OPTIONSET store and push the parsed option sets into the catalogue slice.
 *
 * ⚠️ **Divergent in exactly ONE line, and it is a real one:** web follows the success dispatch
 * with `optionSetsDataCallComplete()`; mobile does not, because mobile's `catalogueSlice` has no
 * such action — verified against core's own variants (`optionSetsDataCallComplete` appears in
 * `catalogueSlice/index.desktop.ts` and is absent from `index.native.ts`). That is why this body
 * is duplicated across the variants instead of living in `shared.ts`: a shared body cannot
 * conditionally dispatch an action that only one platform defines.
 *
 * **Collapse this into `shared.ts` the day mobile's slice grows the completion action.**
 */
export function getOptionsetOffline() {
  let fetchedItems: any[] = [];

  get({
    domain: "OPTIONSET",
    callbackFn: (items: any) => {
      items?.forEach(function (item: any) {
        fetchedItems.push(JSON.parse(item));
      });
      store.dispatch(getOptionSetsDataSuccess({ response: fetchedItems }));
      store.dispatch(optionSetsDataCallComplete());
    },
  });
}

/**
 * Read the local ITEM store and push the parsed items into the items slice.
 *
 * ⚠️ Same single-line divergence as `getOptionsetOffline` above — web adds
 * `itemsDataCallComplete()`, which `ItemsSlice/index.native.ts` does not define. See that
 * function's note.
 */
export function getItemsOffline() {
  let fetchedItems: any[] = [];

  get({
    domain: "ITEM",
    callbackFn: (items: any) => {
      items?.forEach(function (item: any) {
        fetchedItems.push(JSON.parse(item));
      });
      store.dispatch(getItemsDataSuccess(fetchedItems));
      store.dispatch(itemsDataCallComplete());
    },
  });
}

/**
 * Decide which locally-stored sales can be dropped, and whether any still need syncing.
 *
 * ⚠️ **WEB-ONLY** — the other platform has no such function at all (not commented out: genuinely
 * absent, confirmed by parsing its `helper/OfflineHelper.js`).
 *
 * ⚠️ A sale is removable only if it is NOT `TO_BE_SYNCED` **and** older than its cleanup
 * interval. The `else if` is what enforces that: an unsynced sale sets the flag and is never
 * considered for removal, however old it is. Do not flatten the branches.
 *
 * ⚠️ Dates are compared in the BUSINESS's timezone, falling back to `Asia/Calcutta`. That
 * fallback is the app's own and is load-bearing — a terminal whose business data has not loaded
 * yet still ages sales, just against IST.
 *
 * The commented-out `saleVos` lines are pre-monorepo residue and are preserved verbatim.
 */
function filterOutdatedSyncedSales(sales: any) {
  const businessStore = store.getState().businessStore,
    businessData = businessStore?.businessData || {};
  let sale: any,
    removableSaleIds: any[] = [],
    // saleVos = [],
    operation,
    unsyncedSalesExist = false,
    cleanupInterval,
    dateNow = moment().tz(businessData?.timeZone?.zoneId || "Asia/Calcutta");

  sales.forEach(function (item: any) {
    sale = JSON.parse(item);
    operation = sale.operation;
    cleanupInterval =
      sale.saleStatus === SALE_STATUS_TYPES.OPEN
        ? ORDERS_CLEANUP_INTERVALS.openOrder
        : ORDERS_CLEANUP_INTERVALS.otherOrder;
    if (sale.syncStatus === SALE_SYNC_STATUSES.TO_BE_SYNCED) {
      unsyncedSalesExist = true;
    } else if (
      sale.updatedBy &&
      sale.updatedBy.date &&
      dateNow.diff(sale.updatedBy.date) > cleanupInterval
    ) {
      // Removing unchanged sales older than 5 days.
      removableSaleIds.push(sale.mid);
      return;
    }
    // saleVos.push(Pos.SaleHelper.getSaleVo(sale));
  });
  return {
    removableSaleIds,
    unsyncedSalesExist,
    // saleVos
  };
}

/**
 * ⚠️ **This function is ONE LIVE LINE and a wall of commented-out code.** All it does today is
 * `dispatch(setSyncData(true))`; going offline, toasting, re-login and the viewport event are all
 * commented out upstream. Transcribed verbatim — the comments record what the sync flow used to
 * do and are the only documentation of it.
 */
function syncUncsyncedSales() {
  // Go offline if there are any unsynced sales left, ie sales that were modified locally,
  // but not yet saved to the server.
  store.dispatch(setSyncData(true))

  // console.log('isOffline', businessStore)
  // ToastNotifyError(pos_errormap["UI_MSG_GOING_OFFLINE_SALES_EXIST"]);
  // setOffline(true);
  // setLocalStorage(LOCALSTORAGE_KEYS.isOffline, true);
  // const itemsData = store.getState().itemsStore?.itemsData || {},
  //   channelDef = store.getState().catalogStore?.channelDef || [];
  // if (!Object.keys(itemsData)?.length || !channelDef?.length) {
  //   offlineloginSuccess();
  // }
  // setTimeout(function () {
  //   Ext.Viewport.fireEvent("goonline");
  // }, 1000);
}

/**
 * The startup sweep: load every local sale, drop the stale ones, and go offline if any still
 * need syncing. Called once from the login saga.
 *
 * ⚠️ **WEB-ONLY**, and session 26 deferred it for exactly this closure — it needs the three
 * functions above plus `isOffline` and a raw `'ATOKEN'` read.
 *
 * ⚠️ **The token is read by the STRING LITERAL `'ATOKEN'`, not via `LOCALSTORAGE_KEYS`.** That is
 * the app's own inconsistency and is preserved: `LOCALSTORAGE_KEYS.aToken` happens to equal
 * `"ATOKEN"` today, but substituting it would be an unmeasured change to an offline gate.
 */
export function checkOfflineOperatedSales() {
  var unsyncedSalesExist = false,
    removableSaleIds: any = [];

  const callback = function (sales: any, error?: any) {
    if (error) {
      // Rista.CommonHelper.handleOfflineError(error);
      return;
    }
    if (!isEmpty(sales)) {
      const data = filterOutdatedSyncedSales(sales);
      removableSaleIds = data.removableSaleIds;
      unsyncedSalesExist = data.unsyncedSalesExist;
    }
    if (!isEmpty(removableSaleIds)) {
      console.log("removableSaleIds=", removableSaleIds);
      removeOfflineSale({ saleId: removableSaleIds });
    } else {
      console.log("removableSaleIds=[] No Removable Sale IDs");
    }
    if (unsyncedSalesExist && !isOffline() && getLocalStorage('ATOKEN')) {
      syncUncsyncedSales();
    }
  };
  fetchAllOfflineSales({ cb: callback });
}

/**
 * Tears down an OFFLINE session: clears the offline-login flag and marks the logout succeeded.
 *
 * 🔴 **PER-VARIANT, and the difference is one line that must not be shared.** This half then calls
 * the host's logout teardown; the other platform's half stops after the two dispatches. Sharing
 * the body would be actively wrong rather than merely untidy: the effects bridge's `logout` is
 * wired to a DIFFERENT function on each platform — here it is the app's `handleLogoutAction`,
 * there it stops audio and resets navigation — so a shared body would start doing something on
 * the other platform that its source has never done.
 *
 * ⚠️ Takes no parameters even though both call sites pass one; that is the app's own shape,
 * preserved.
 */
export function logoutOffline() {
  store.dispatch(updateOfflineLogin({
    offlineLogin: false
  }))
  store.dispatch(logoutUserSuccess({
    status: "success"
  }))
  logout();
}

/**
 * Read one sale out of the local SALE store and hand it to the caller.
 *
 * 🔴 **PER-VARIANT on two differences:** this side is SILENT when the sale is missing (it only
 * calls back with an error code), and it does NOT push the fetched sale into the store. The other
 * platform toasts "Sale not found!" and dispatches the sale into `saleDataStore` as a side effect
 * of reading it.
 */
export function fetchOfflineSale(params: any) {
  const { saleId, callback } = params;

  get({
    domain: "SALE",
    keys: saleId,
    callbackFn: (sale: any, error: any) => {
      if (error) {
        handleOfflineError(error);
        return;
      }
      if (!sale) {
        //Ext.Msg.alert('', 'Sale not found!');
        callback({ error: "UI_ERR_OFFLINE_SALE_NOT_FOUND" });
        return;
      }
      if (callback) {
        if (!Array.isArray(sale)) {
          sale = JSON.parse(sale);
          console.log("===============ticketGroup fetching offline sale", params, sale);
        }
        callback(sale);
      }
    },
  });
}

/**
 * Cancel a sale offline: fetch it, flag it `Cancelled` + to-be-synced, attach the reason, re-stamp
 * its metadata and write it back.
 *
 * 🔴 **PER-VARIANT on the SHAPE OF `statusInfo`, which is a payload difference, not a style one:**
 * this side assigns the reason STRING (`sale.statusInfo = reason`); the other assigns an OBJECT
 * `{ reason, remarks }`. Whatever consumes `statusInfo` therefore sees different types per
 * platform. Both are transcribed as-is. This side is also silent when the sale is missing.
 *
 * ⚠️ `remarks` is destructured here and never used — dead on this platform precisely BECAUSE of
 * the string form above. Kept, so the divergence is visible at the site.
 */
export function cancelSaleOffline(params: any) {
  const { mid, callback, reason, remarks } = params;

  fetchOfflineSale({
    saleId: mid,
    callback: (sale: any) => {
      if (!sale) {
        // Ext.Msg.alert('', 'Sale not found!');
        return;
      }
      sale.syncStatus = "tobesynced";
      sale.operation = "SAVE_SALE";
      sale.saleStatus = "Cancelled";
      if (!isEmpty(reason)) {
        // sale.statusInfo = {
        //   reason,
        //   remarks
        // };
        sale.statusInfo = reason;
      }
      sale = populateMetaInfo(sale);
      saveSaleLocally({
        sale,
        callback,
      });
    },
  });
}

/**
 * Void a sale offline. Same shape as `cancelSaleOffline`, with `saleStatus` `Voided`.
 *
 * 🔴 **PER-VARIANT for the same `statusInfo` reason** (string here, object on the other platform),
 * and this side is silent on a missing sale.
 *
 * ⚠️ **The callback fires TWICE on the reason path, and only there.** Inside `if (!isEmpty(reason))`
 * the apps call `params.callback()` with NO argument, and `saveSaleLocally` then calls the same
 * callback with the saved sale. Both platforms do this; preserved.
 */
export function voidSaleOffline(params: any) {
  const { id, reason, remarks } = params;
  const cb = params.callback;

  fetchOfflineSale({
    saleId: id,
    callback: (sale: any) => {
      if (!sale) {
        // Ext.Msg.alert('', 'Sale not found!');
        return;
      }
      sale.syncStatus = "tobesynced";
      sale.operation = "SAVE_SALE";
      sale.saleStatus = "Voided";
      if (!isEmpty(reason)) {
        sale.statusInfo = reason;
        if (params.callback) {
          params.callback();
        }
      }
      sale = populateMetaInfo(sale);
      saveSaleLocally({
        sale,
        callback: cb,
      });
    },
  });
}

